import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import { useSnackbar } from 'notistack';

import { getListAllAPI, getOneAPI } from '../../services';
import DialogoCartazesProdutos from '../Produtos/DialogoCartazesProdutos';
import DialogoSelecionarCopias from './DialogoSelecionarCopias';
import { registrarLogCartaz } from './observabilidade';

/**
 * Busca, numa única chamada, os dados que o cartaz A4 precisa para os produtos
 * selecionados: campos do cartaz + `unidade: { tipo }` + `ofertasProdutos: [{ tipo, valor }]`
 * ativas. Endpoint enxuto e dedicado — não traz a linha inteira de `produtos`.
 */
async function buscarProdutosParaCartaz(ids) {
  const { data } = await getListAllAPI(
    'cartazes_oferta',
    ['id', 'asc'],
    { id: ids },
    [],
  );
  return data;
}

/** Título exibido na tarja vermelha do cartaz (config global, linha 1 de `config`). */
async function buscarTituloCartaz() {
  const { data } = await getOneAPI('config', 1, []);
  return data.titulo_cartaz;
}

/**
 * Orquestra a geração de cartazes A4 a partir de ids de produto:
 * `handleAbrir(ids)` -> carrega os produtos -> valida `descricao_no_cartaz` ->
 * diálogo de cópias -> abre o visualizador de PDF (`DialogoCartazesProdutos`).
 * Não persiste nada (rastreabilidade só via `registrarLogCartaz`).
 */
const ContainerDialogoGerarCartazes = forwardRef((props, ref) => {
  const carregando = useRef(false);
  const refDialogoCopias = useRef(null);
  const refDialogoCartazes = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const liberar = () => {
    carregando.current = false;
  };

  async function prepararSelecao(ids) {
    try {
      const produtos = await buscarProdutosParaCartaz(ids);

      const semDescricao = produtos.filter((p) => !p.descricao_no_cartaz);
      const aptos = produtos.filter((p) => p.descricao_no_cartaz);

      if (semDescricao.length) {
        registrarLogCartaz('sem_descricao', {
          ids: semDescricao.map((p) => p.id),
          nomes: semDescricao.map((p) => p.nome),
        });
        enqueueSnackbar(
          `Sem descrição de cartaz cadastrada: ${semDescricao
            .map((p) => p.nome)
            .join(', ')}`,
          { variant: 'warning' },
        );
      }

      if (!aptos.length) {
        enqueueSnackbar('Nenhum produto apto para gerar cartaz.', {
          variant: 'error',
        });
        liberar();
        return;
      }

      registrarLogCartaz('abrir_selecao', {
        total: aptos.length,
        ids: aptos.map((p) => p.id),
      });
      refDialogoCopias.current.handleOpen(aptos);
    } catch (e) {
      liberar();
      registrarLogCartaz('gerar_erro', {
        contexto: 'carregar_produtos',
        erro: e ? e.toString() : 'desconhecido',
      });
      enqueueSnackbar(e ? e.toString() : 'Erro ao carregar os produtos.', {
        variant: 'error',
      });
    }
  }

  async function gerar(produtosComCopias) {
    try {
      registrarLogCartaz('gerar_inicio', {
        total: produtosComCopias.length,
        ids: produtosComCopias.map((p) => p.id),
      });
      const titulo = await buscarTituloCartaz();
      refDialogoCartazes.current.handleOpen(produtosComCopias, titulo);
      registrarLogCartaz('gerar_sucesso', { total: produtosComCopias.length });
    } catch (e) {
      liberar();
      registrarLogCartaz('gerar_erro', {
        contexto: 'montar_pdf',
        erro: e ? e.toString() : 'desconhecido',
      });
      enqueueSnackbar(e ? e.toString() : 'Erro ao gerar os cartazes.', {
        variant: 'error',
      });
    }
  }

  useImperativeHandle(ref, () => ({
    handleAbrir(ids) {
      if (carregando.current || !ids || !ids.length) {
        return;
      }
      carregando.current = true;
      prepararSelecao(ids);
    },
  }));

  return (
    <>
      <DialogoSelecionarCopias
        ref={refDialogoCopias}
        onConfirmar={gerar}
        onCancelar={liberar}
      />
      <DialogoCartazesProdutos ref={refDialogoCartazes} handleClose={liberar} />
    </>
  );
});

export default ContainerDialogoGerarCartazes;
